import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Paper,
  Avatar,
  Divider,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  FaRocket,
  FaEye,
  FaUsers,
  FaFileAlt,
  FaCheckCircle,
  FaEdit,
  FaPhone,
  FaDatabase,
  FaAddressBook,
  FaListAlt,
} from "react-icons/fa";
import { MdCheckCircle } from "react-icons/md";
import { styled, alpha } from "@mui/material/styles";
import { getHeader, toastError } from "../../utils/util";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import AutoSelectTemplate from "../../widgets/AutoSelectTemplate";
import RcsTemplateById from "./RcsTemplateById";
import { LoadingButton } from "@mui/lab";
import RcsDatabaseAudience from "./RcsDatabaseAudience";
import { buildRcs2CampaignPayload, getRcsAudienceError } from "./rcs2AudienceUtils";
import ContactsTabs from "./ContactsTabs";

// ─── Styled ───────────────────────────────────────────────────────────────────

const StyledCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 16,
  boxShadow: "none",
  marginBottom: theme.spacing(3),
}));

const StepIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "completed",
})(({ theme, active, completed }) => ({
  width: 36, height: 36, borderRadius: "50%",
  background: active || completed ? theme.palette.primary.main : theme.palette.grey[100],
  color: active || completed ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
}));

const SummaryBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  background: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  marginBottom: theme.spacing(2.5),
}));

const SummaryItem = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" gap={0.75}>
    <Box sx={{ color: "primary.main", fontSize: 13, display: "flex" }}>
      {icon}
    </Box>
    <Typography variant="caption" color="textSecondary">
      {label}:
    </Typography>
    <Typography variant="caption" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

// ─── Contact source helper ────────────────────────────────────────────────────

function contactSourceLabel(campaignData, selectedCount) {
  if (campaignData.selectedGroup) {
    const rangeText =
      campaignData.startIndex !== undefined &&
      campaignData.endIndex !== undefined
        ? ` · ${campaignData.startIndex}–${campaignData.endIndex}`
        : "";
    if (campaignData.dbGroup === "contacts")
      return {
        icon: <FaAddressBook />,
        label: "Contact Group",
        text: `${campaignData.selectedGroupLabel || campaignData.selectedGroup}${campaignData.contactSelectionMode === "ai" ? " · AI selection" : rangeText}`,
      };
    if (campaignData.dbGroup === "database")
      return {
        icon: <FaDatabase />,
        label: "Database List",
        text: `${campaignData.selectedGroupLabel || campaignData.selectedGroup}${campaignData.contactSelectionMode === "ai" ? " · AI selection" : rangeText}`,
      };
    return {
      icon: <FaListAlt />,
      label: "Group",
      text: campaignData.selectedGroup,
    };
  }
  if (campaignData.contacts.length > 0)
    return {
      icon: <FaUsers />,
      label: "Contacts",
      text: `${selectedCount} / ${campaignData.contacts.length} selected`,
    };
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Rcs2CampaignLauncher = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Campaign data (confirmed/submitted values) ───────────────────────────

  const [campaignData, setCampaignData] = useState({
    campaignName: "",
    template: "",
    message: "",
    contacts: [],
    uploadedFile: null,
    templateContent: null,
  });
  const [contactGroupName, setContactGroupName] = useState("");
  const [campaignGroupName, setCampaignGroupName] = useState("");

  // ── Transient ContactsTabs UI state — lifted so it persists on Back→Next ─
  //    Nothing here is "confirmed"; it's just what the user is currently
  //    looking at / typing in inside the Contacts step.

  const [contactsTabState, setContactsTabState] = useState({
    tabValue: 0,
    // Tab 1 – Database
    selectedList: "",
    dbColumns: [],
    dbStartIndex: "",
    dbEndIndex: "",
    // Tab 3 – Contacts List
    pendingGroup: "",
    groupSearchQuery: "",
    groupStartIndex: "",
    groupEndIndex: "",
  });

  // ── Stepper ──────────────────────────────────────────────────────────────

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Campaign setup", "Choose audience", "Review & launch"];

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  // ── Misc UI state ────────────────────────────────────────────────────────

  const [isDragged, setIsDragged] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const contactsT = searchParams.get("contactsType");
    setContactGroupName(searchParams.get("name"));
    setCampaignGroupName(searchParams.get("groupName"));
    setContactsTabState((prev) => ({
      ...prev,
      tabValue:
        contactsT === "contacts" ? 3 : contactsT === "database" ? 1 : 0,
    }));
  }, [searchParams]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const selectedCount = campaignData.contacts.filter((c) => c.selected).length;
  const allSelected =
    selectedCount === campaignData.contacts.length &&
    campaignData.contacts.length > 0;
  const indeterminate = selectedCount > 0 && !allSelected;
  const filteredContacts = campaignData.contacts.filter((c) =>
    String(c.phone).includes(searchQuery)
  );

  const step0Done = !!campaignData.template && !!campaignData.campaignName.trim();
  const audienceError = getRcsAudienceError(campaignData);
  const step1Done = !audienceError;
  const contactSource = contactSourceLabel(campaignData, selectedCount);

  const nextDisabledReason = (() => {
    if (activeStep === 0) {
      if (!campaignData.campaignName.trim())
        return "Enter a campaign name to continue";
      if (!campaignData.template) return "Select a template to continue";
    }
    if (activeStep === 1) {
      if (audienceError) return audienceError;
    }
    return null;
  })();

  // ── File / drag handlers (unchanged) ─────────────────────────────────────

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["phoneNumber", "name"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "contacts_template.xlsx");
  };

  const handleFileUpload = (fileOrEvent, duplicateChecker) => {
    const file =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];
    if (!file) return;
    if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setCampaignData((prev) => ({ ...prev, uploadedFile: file }));
      setUploadProgress(0);
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (e.lengthComputable)
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        let contacts = parsedData
          .slice(1)
          .map((row, index) => ({
            id: index + 1,
            phone: String(row[0] || ""),
            name: row[1] ? String(row[1]) : "Unknown",
            selected: true,
          }))
          .filter((c) => c.phone);
        if (duplicateChecker && typeof duplicateChecker === "function")
          contacts = duplicateChecker(contacts);
        setCampaignData((prev) => ({
          ...prev,
          contacts: [...prev.contacts, ...contacts],
          selectedGroup: "",
        }));
        setUploadProgress(100);
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragged(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragged(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragged(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileUpload({ target: { files: [files[0]] } });
  };

  const removeContact = (id) =>
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((c) => c.id !== id),
    }));

  const toggleSelect = (id) =>
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) =>
        c.id === id ? { ...c, selected: !c.selected } : c
      ),
    }));

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => ({ ...c, selected: checked })),
    }));
  };

  // ── Launch (unchanged logic + dbGroup in payload) ────────────────────────

  const launchCampaign = async () => {
    if (sendingCampaign) return;
    if (!campaignData.campaignName.trim()) {
      toast.error("Campaign name is required!");
      return;
    }
    if (!campaignData.template) {
      toast.error("Template is required!");
      return;
    }
    if (audienceError) {
      toast.error(audienceError);
      return;
    }
    try {
      setSendingCampaign(true);
      const response = await axios.post(
        `${API_ENDPOINT}/rcs/launch-campaign-2`,
        buildRcs2CampaignPayload(campaignData, { contactGroupName, campaignGroupName }),
        getHeader()
      );
      toast.success(response.data.message);
      setTimeout(() => navigate(`/home/queue-monitor?type=rcs`), 700);
    } catch (error) {
      toastError(error);
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleCampaignName = useCallback((e) => {
    const value = e.target.value;
    if (/^[A-Za-z0-9_ ]*$/.test(value) && value.length <= 100)
      setCampaignData((prev) => ({ ...prev, campaignName: value }));
  }, []);

  // ── Step content ──────────────────────────────────────────────────────────

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField
              label="Campaign name"
              type="text"
              sx={{ mb: 3 }}
              fullWidth
              helperText="Use a name you can find later in your reports. Letters, numbers, spaces and underscores; up to 100 characters."
              value={campaignData.campaignName}
              onChange={handleCampaignName}
            />
            <Typography fontWeight={600}>Message template</Typography>
            <Box my={2}>
              <AutoSelectTemplate
                label="Select Template"
                value={selectedTemplate}
                onChange={(newV) => {
                  setCampaignData((prev) => ({ ...prev, template: newV }));
                  setSelectedTemplate(newV);
                }}
              />
            </Box>
            {selectedTemplate && (
              <RcsTemplateById
                id={selectedTemplate.templateId}
                showLess={true}
              />
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            {/* Recap of step 0 */}
            {step0Done && (
              <SummaryBar>
                <SummaryItem
                  icon={<FaFileAlt />}
                  label="Campaign"
                  value={campaignData.campaignName}
                />
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <SummaryItem
                  icon={<FaCheckCircle />}
                  label="Template"
                  value={campaignData.template?.templateName || "—"}
                />
              </SummaryBar>
            )}
            <ContactsTabs
              databaseAudience={<RcsDatabaseAudience
                campaignData={campaignData} setCampaignData={setCampaignData}
                tabState={contactsTabState} setTabState={setContactsTabState}
                contactGroupName={contactGroupName}
              />}
              campaignData={campaignData}
              setCampaignData={setCampaignData}
              tabState={contactsTabState}
              setTabState={setContactsTabState}
              isDragged={isDragged}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleFileUpload={handleFileUpload}
              downloadTemplate={downloadTemplate}
              uploadProgress={uploadProgress}
              selectedCount={selectedCount}
              indeterminate={indeterminate}
              allSelected={allSelected}
              handleSelectAll={handleSelectAll}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredContacts={filteredContacts}
              toggleSelect={toggleSelect}
              removeContact={removeContact}
              campaignGroupName={campaignGroupName}
              contactGroupName={contactGroupName}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              {campaignGroupName ? "This template will be saved to your campaign group." : "Review your template and audience. Launching will queue messages for sending."}
            </Alert>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main" }}><FaPhone /></Avatar>
              <Typography variant="h6" fontWeight={700}>Message preview</Typography>
              <Button sx={{ ml: "auto" }} onClick={() => setActiveStep(0)}>Edit template</Button>
            </Box>
            {selectedTemplate && <RcsTemplateById id={selectedTemplate.templateId} showLess />}
            {campaignData.audienceCount > 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your selection includes {campaignData.audienceCount.toLocaleString()} database records. Invalid and duplicate phone numbers are removed when launching.
            </Typography>}
          </Box>
        );

      default:
        return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const descriptions = [
    "Give your campaign a name and choose the message your audience will receive.",
    "Import contacts, paste a quick list, or select an audience from your saved data.",
    "Check the message and recipients before sending.",
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 1.5, sm: 3 }, py: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar variant="rounded" sx={{ bgcolor: "primary.main", width: 48, height: 48, borderRadius: 3 }}><FaRocket /></Avatar>
        <Box>
          <Typography variant="overline" color="text.secondary">RCS messaging</Typography>
          <Typography variant="h4" component="h1" fontWeight={750} sx={{ fontSize: { xs: 25, sm: 32 } }}>Create a campaign</Typography>
        </Box>
        <Chip label={`Step ${activeStep + 1} of 3`} variant="outlined" sx={{ ml: "auto", display: { xs: "none", sm: "flex" } }} />
      </Box>
      <StyledCard>
        <CardContent sx={{ py: 2.5 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => <Step key={label}>
              <StepLabel StepIconComponent={() => <StepIcon active={index === activeStep} completed={index < activeStep}>
                {index < activeStep ? <MdCheckCircle size={22} /> : index + 1}
              </StepIcon>}>
                <Typography variant="body2" fontWeight={index === activeStep ? 700 : 400}>{label}</Typography>
              </StepLabel>
            </Step>)}
          </Stepper>
        </CardContent>
      </StyledCard>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 280px" }, gap: 3, alignItems: "start" }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, minWidth: 0 }}>
          <Typography component="h2" variant="h6" fontWeight={700}>{steps[activeStep]}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>{descriptions[activeStep]}</Typography>
          {renderStepContent(activeStep)}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mt: 3, pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
            <Button onClick={handleBack} disabled={activeStep === 0 || sendingCampaign} startIcon={<FaEdit />}>Back</Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {nextDisabledReason && activeStep < 2 && <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>{nextDisabledReason}</Typography>}
              {activeStep === 2 ? <LoadingButton variant="contained" loading={sendingCampaign} disabled={!step0Done || !step1Done} onClick={launchCampaign} startIcon={<FaRocket />} size="large" sx={{ borderRadius: 2 }}>
                {campaignGroupName ? "Save campaign" : "Launch campaign"}
              </LoadingButton> : <Tooltip title={nextDisabledReason || ""}><span><Button variant="contained" size="large" onClick={handleNext} disabled={!!nextDisabledReason} endIcon={activeStep === 1 ? <FaEye /> : <FaUsers />} sx={{ borderRadius: 2 }}>
                {activeStep === 1 ? "Review campaign" : "Choose audience"}
              </Button></span></Tooltip>}
            </Box>
          </Box>
        </Paper>
        <Paper component="aside" variant="outlined" sx={{ order: { xs: activeStep === 2 ? -1 : 1, lg: 1 }, p: 2.5, borderRadius: 3, position: { lg: "sticky" }, top: 24, minWidth: 0 }}>
          <Typography fontWeight={700} sx={{ mb: 2 }}>Campaign summary</Typography>
          <Typography variant="caption" color="text.secondary">Campaign name</Typography>
          <Typography sx={{ mb: 2, overflowWrap: "anywhere" }}>{campaignData.campaignName || "Not named yet"}</Typography>
          <Typography variant="caption" color="text.secondary">Template</Typography>
          <Typography sx={{ mb: 2, overflowWrap: "anywhere" }}>{campaignData.template?.templateName || "Not selected yet"}</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">Audience</Typography>
          <Typography sx={{ overflowWrap: "anywhere" }}>{contactSource?.text || "Choose your recipients"}</Typography>
          {campaignData.audienceCount > 0 && campaignData.selectedGroup && <Chip label={`${campaignData.audienceCount.toLocaleString()} selected records`} size="small" sx={{ mt: 1 }} />}
          {activeStep === 2 && <Button size="small" onClick={() => setActiveStep(1)} disabled={sendingCampaign} sx={{ mt: 1 }}>Edit audience</Button>}
          {campaignGroupName && <Alert severity="info" sx={{ mt: 2 }}>Saving to {campaignGroupName}. The campaign group supplies the audience.</Alert>}
        </Paper>
      </Box>
    </Box>
  );
};

export default Rcs2CampaignLauncher;
